import {
  useEffect,
  useRef,
  useState,
  ReactNode,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';

// Services
import { useSingIn } from '../modules/auth/services/signIn';
import { useGetUserProfile } from '../modules/profile/services/getUserProfile';

import { useGetBasket } from '../modules/basket/services/getBasket';
import { useGetAddresses } from '../modules/addresses/services/getAddresses';

// Store
import { userInfo } from '../store/selectors/user';

// Components
import { Welcome } from '../components/Welcom';
import { setWelcomeVisible } from '../lib/welcomeScreen';
import { useGetNearesBranch } from '../modules/addresses/services/getNearBranch';

// Analytics
import {
  useMindboxCartSync,
  useMindboxTracking,
} from '../lib/useMindboxTracking';

// Constants
const WELCOME_TIMEOUT = 1500;
const DEFAULT_LANGUAGE = 'uz';
const STORAGE_KEYS = {
  WELCOME: 'welcome',
  LANGUAGE: 'language',
  TOKEN: 'token',
  BRANCH: 'branch',
  LOGGED_OUT: 'loggedOut',
} as const;

// Первый вход за сессию — показываем заставку. Считается один раз при
// загрузке модуля, а не в инициализаторе useState: StrictMode вызывает его
// дважды, и второй вызов уже видел бы выставленный флаг. Флаг видимости
// ставим здесь же, до первого рендера экранов: главная должна сразу знать,
// что заставка на экране, и не открывать системные окна под ней.
const isFirstVisit =
  sessionStorage.getItem(STORAGE_KEYS.WELCOME) !== 'true';
if (isFirstVisit) {
  sessionStorage.setItem(STORAGE_KEYS.WELCOME, 'true');
}
setWelcomeVisible(isFirstVisit);

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider = ({ children }: AppProviderProps) => {
  // Initialize global services
  const { nearest } = useGetNearesBranch();
  const branchId = nearest?.branch_id ?? 0;
  sessionStorage.setItem(STORAGE_KEYS.BRANCH, branchId.toString());

  useGetBasket();
  useGetAddresses();

  // Mindbox через бэкенд: авторизация устройства, user-info и состав корзины.
  useMindboxTracking();
  useMindboxCartSync();

  // Selectors
  const user = useSelector(userInfo);

  // Memoized values
  const initData = useMemo(() => window.Telegram?.WebApp?.initData || '', []);

  // Services
  const { signIn } = useSingIn();

  useGetUserProfile();

  // Refs
  const didSignIn = useRef(false);

  // Local state with better initialization
  const [showWelcome, setShowWelcome] = useState(isFirstVisit);

  // Initialize default language
  useEffect(() => {
    const currentLanguage = localStorage.getItem(STORAGE_KEYS.LANGUAGE);
    if (!currentLanguage) {
      localStorage.setItem(STORAGE_KEYS.LANGUAGE, DEFAULT_LANGUAGE);
    }
  }, []);

  // Auto sign-in using Telegram initData
  useEffect(() => {
    if (didSignIn.current || !initData) return;

    const hasToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const hasLoggedOut = localStorage.getItem(STORAGE_KEYS.LOGGED_OUT) === 'true';

    if (!user.isAuth && !hasToken && !hasLoggedOut && initData) {
      didSignIn.current = true;
      signIn(initData);
    }
  }, [user.isAuth, signIn, initData]);

  // Заставка при первом входе за сессию — слоем поверх приложения: под ней
  // уже грузится главная, и заставка плавно растворяется в неё.
  return (
    <>
      {children}
      {showWelcome && (
        <Welcome
          holdMs={WELCOME_TIMEOUT}
          onDone={() => {
            setShowWelcome(false);
            setWelcomeVisible(false);
          }}
        />
      )}
    </>
  );
};
